import { defineStore } from 'pinia'
import { ref } from 'vue'
import { io } from 'socket.io-client'

let devConnectionLogged = false

export const useSocketStore = defineStore('socket', () => {
  const socket = ref(null)
  const connected = ref(false)
  const onlineUsers = ref(new Set())
  const currentUserId = ref(null)

  const connect = () => {
    // Prevent multiple connections - if socket already exists, don't recreate
    if (socket.value) {
      // Already connected or connecting — nothing to do
      if (socket.value.connected) return
      // Socket exists but disconnected — reconnect it
      socket.value.connect()
      return
    }
    
    // Get base URL from environment or use relative path for production
    let baseURL
    let socketPath
    
    // Check if we're in development mode and should use the proxy
    if (import.meta.env.DEV) {
      // Development mode - connect directly to backend
      baseURL = 'http://localhost:5001'  // Backend server port
      socketPath = '/socket.io/'
      if (!devConnectionLogged) {
        // Console log removed
        devConnectionLogged = true
      }
    } else if (import.meta.env.VITE_API_URL) {
      try {
        const apiUrl = new URL(import.meta.env.VITE_API_URL)
        // Ensure we only use scheme+host (origin), drop any path like /api
        baseURL = apiUrl.origin
      } catch (e) {
        // Fallback: previous behavior
        baseURL = import.meta.env.VITE_API_URL.replace('/api', '')
      }
      // Use /socket.io path
      socketPath = '/socket.io/'
    } else {
      // Production fallback - use same domain
      baseURL = window.location.origin
      socketPath = '/socket.io/'
    }
    
    socket.value = io(baseURL, {
      path: socketPath,
      transports: ['websocket', 'polling'],
      timeout: 20000,
      forceNew: false,
      autoConnect: true,
      reconnection: true,
      reconnectionDelay: 2000,
      reconnectionAttempts: Infinity,
      reconnectionDelayMax: 10000
    })

    socket.value.on('connect', () => {
      connected.value = true
      // Сервер қайта іске қосылғанда join event-ін қайта жіберу
      if (currentUserId.value) {
        socket.value.emit('join', currentUserId.value)
      }
    })

    socket.value.on('disconnect', () => {
      connected.value = false
    })

    socket.value.on('connect_error', (error) => {
      // Socket connection error
    })

    // Handle online users list (only update if changed)
    socket.value.on('online_users', (userIds) => {
      const currentUsers = Array.from(onlineUsers.value)
      const newUsers = userIds.sort()
      const hasChanged = JSON.stringify(currentUsers.sort()) !== JSON.stringify(newUsers)
      
      if (hasChanged) {
        onlineUsers.value.clear()
        userIds.forEach(id => onlineUsers.value.add(id))
      }
    })

    // Handle user status updates (only if enabled)
    socket.value.on('user_status_update', ({ userId, status }) => {
      // Silently update user status without forcing any UI updates
      if (status === 'online') {
        onlineUsers.value.add(userId)
      } else if (status === 'offline') {
        onlineUsers.value.delete(userId)
      }
    })
  }

  const disconnect = () => {
    if (socket.value) {
      // Console log removed
      // Remove all listeners before disconnecting
      socket.value.removeAllListeners()
      socket.value.disconnect()
      socket.value = null
      connected.value = false
      onlineUsers.value.clear()
      currentUserId.value = null
    }
  }

  const joinRoom = (userId) => {
    currentUserId.value = userId
    if (socket.value && connected.value) {
      socket.value.emit('join', userId)
    }
  }

  const sendMessage = (messageData) => {
    if (socket.value && connected.value) {
      socket.value.emit('send_message', messageData)
    }
  }

  const onNewMessage = (callback) => {
    if (socket.value) {
      socket.value.on('new_message', callback)
    }
  }

  const onMessageSent = (callback) => {
    if (socket.value) {
      socket.value.on('message_sent', callback)
    }
  }

  const onUserTyping = (callback) => {
    if (socket.value) {
      socket.value.on('user_typing', callback)
    }
  }

  const emitTyping = (receiverId, isTyping) => {
    if (socket.value && connected.value) {
      socket.value.emit('typing', { receiverId, isTyping })
    }
  }

  const markAsRead = (messageIds) => {
    if (socket.value && connected.value) {
      socket.value.emit('mark_as_read', { messageIds })
    }
  }

  const onMessageRead = (callback) => {
    if (socket.value) {
      socket.value.on('message_read', callback)
    }
  }

  const onNotification = (callback) => {
    if (socket.value) {
      socket.value.on('notification', callback)
    }
  }

  const onUserStatusUpdate = (callback) => {
    if (socket.value) {
      socket.value.on('user_status_update', callback)
    }
  }

  // Notification events
  const onNewNotification = (callback) => {
    if (socket.value) {
      socket.value.on('new_notification', callback)
    }
  }

  const onUserRegistered = (callback) => {
    if (socket.value) {
      socket.value.on('user_registered', callback)
    }
  }

  const onPendingUsersUpdate = (callback) => {
    if (socket.value) {
      socket.value.on('pending_users_update', callback)
    }
  }

  const removeAllListeners = () => {
    if (socket.value) {
      socket.value.removeAllListeners()
    }
  }

  // Schedule real-time sync
  const onScheduleChanged = (callback) => {
    if (socket.value) socket.value.on('schedule_changed', callback)
  }
  const offScheduleChanged = (callback) => {
    if (socket.value) socket.value.off('schedule_changed', callback)
  }

  return {
    socket,
    connected,
    onlineUsers,
    connect,
    disconnect,
    joinRoom,
    sendMessage,
    onNewMessage,
    onMessageSent,
    onUserTyping,
    emitTyping,
    markAsRead,
    onMessageRead,
    onNotification,
    onUserStatusUpdate,
    onNewNotification,
    onUserRegistered,
    onPendingUsersUpdate,
    onScheduleChanged,
    offScheduleChanged,
    removeAllListeners
  }
})
